import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import ReactDOMServer from 'react-dom/server';

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return 'Unknown City';
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown City';
};

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
      
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{ 
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Stock Report Display Component
const StockReportDisplay = ({ formData, filters, branchProfile, stockData }) => {
  // Ensure stockData is an array
  const safeStockData = Array.isArray(stockData) ? stockData : [];

  const filteredData = safeStockData.filter(item => {
    let matchesFilters = true;

    if (filters.location.length > 0 && !filters.location.includes("All Warehouse")) {
      matchesFilters = matchesFilters && filters.location.includes(item.location || "Unknown Location");
    }
    if (filters.group.length > 0 && !filters.group.includes("All Groups")) {
      matchesFilters = matchesFilters && filters.group.includes(item.groupName || "No Group Assigned");
    }
    if (filters.product.length > 0 && !filters.product.includes("All Products")) {
      matchesFilters = matchesFilters && filters.product.includes(item.itemDescription || "Unknown Product");
    }

    // Show zero quantities only if "With Zero" is selected
    if (formData.stockType === "with out zero" && item.qty === 0) {
      matchesFilters = false;
    }

    return matchesFilters;
  });

  // Aggregate stock by itemDescription, location, and group
  const aggregatedData = filteredData.reduce((acc, item) => {
    const key = `${item.itemDescription}-${item.location}-${item.groupName}`;
    if (!acc[key]) {
      acc[key] = {
        itemDescription: item.itemDescription,
        location: item.location,
        groupName: item.groupName,
        category: item.category,
        brand: item.brand,
        uom: item.uom,
        qty: 0,
        mrp: parseFloat(item.mrp) || 0,
        value: 0,
        tax: parseFloat(item.tax) || 0,
        taxAmount: parseFloat(item.taxAmount) || 0,
      };
    }
    acc[key].qty += parseFloat(item.qty) || 0;
    acc[key].value += parseFloat(item.value) || 0;
    acc[key].taxAmount += parseFloat(item.taxAmount) || 0;
    return acc;
  }, {});

  const sortedData = Object.values(aggregatedData).sort((a, b) => 
    a.itemDescription.localeCompare(b.itemDescription) || a.location.localeCompare(b.location)
  );

  const totalQty = sortedData.reduce((sum, item) => sum + item.qty, 0);
  const totalTaxAmount = sortedData.reduce((sum, item) => sum + item.taxAmount, 0);
  const totalValue = sortedData.reduce((sum, item) => sum + item.value, 0);

  const reportTitle = 'Opening Stock Report As on';
  const city = branchProfile.city || getCityFromAddress(branchProfile.address);

  // Adjust table structure based on stockType
  if (formData.stockType === "with out zero") {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        marginTop: '20px',
        border: '2px solid #3b82f6',
        borderRadius: '12px',
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        pageBreakInside: 'avoid',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
      }} className="print-only">
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '2px solid #e2e8f0'
        }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              margin: '0', 
              marginBottom: '15px',
              color: '#1e293b',
              textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
            }}>
              {branchProfile.branchName}
            </h1>
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
              <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
                {reportTitle}
              </div>
              <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                <span><strong>Date:</strong> {new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium' })}</span>
              </div>
            </div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
            color: 'white',
            padding: '15px 25px',
            borderRadius: '15px',
            textAlign: 'center',
            minWidth: '150px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{city}</div>
            <div style={{ fontSize: '12px', marginTop: '5px', opacity: '0.9' }}>
              Option: Abstract
            </div>
          </div>
        </div>

        {/* Stock Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse', 
            fontSize: '11px', 
            border: '2px solid #3b82f6',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
                <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
                <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product Name</th>
                <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Quantity</th>
                {/* <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Rate</th> */}
                <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Amt</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{
                    border: '1px solid #cbd5e1',
                    padding: '30px',
                    textAlign: 'center',
                    color: '#64748b',
                    fontStyle: 'italic',
                    fontSize: '14px',
                    backgroundColor: '#f8fafc'
                  }}>
                    No stock data found for the selected criteria
                  </td>
                </tr>
              ) : (
                sortedData.map((item, index) => {
                  const netRate = item.qty > 0 ? (item.value / item.qty).toFixed(2) : '0.00';
                  const netAmt = item.value.toFixed(2);
                  return (
                    <tr key={`${item.itemDescription}-${item.location}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.itemDescription || 'Unknown Product'}</td>
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.qty.toFixed(2)}</td>
                      {/* <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{netRate}</td> */}
                      <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{netAmt}</td>
                    </tr>
                  );
                })
              )}
              {sortedData.length > 0 && (
                <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
                  <td colSpan="2" style={{
                    border: '1px solid #cbd5e1',
                    padding: '10px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    Total
                  </td>
                  <td style={{
                    border: '1px solid #cbd5e1',
                    padding: '10px',
                    textAlign: 'right',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>{totalQty.toFixed(2)}</td>
                  {/* <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>N/A</td> */}
                  <td style={{
                    border: '1px solid #cbd5e1',
                    padding: '10px',
                    textAlign: 'right',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>{totalValue.toFixed(2)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (formData.stockType === "with zero") {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        marginTop: '20px',
        border: '2px solid #3b82f6',
        borderRadius: '12px',
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        pageBreakInside: 'avoid',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
      }} className="print-only">
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '2px solid #e2e8f0'
        }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              margin: '0', 
              marginBottom: '15px',
              color: '#1e293b',
              textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
            }}>
              {branchProfile.branchName}
            </h1>
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
              <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
                {reportTitle}
              </div>
              <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                <span><strong>Date:</strong> {new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium' })}</span>
              </div>
            </div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
            color: 'white',
            padding: '15px 25px',
            borderRadius: '15px',
            textAlign: 'center',
            minWidth: '150px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{city}</div>
            <div style={{ fontSize: '12px', marginTop: '5px', opacity: '0.9' }}>
              Option: Abstract
            </div>
          </div>
        </div>

        {/* Stock Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse', 
            fontSize: '11px', 
            border: '2px solid #3b82f6',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
                <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
                <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product Name</th>
                <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.length === 0 ? (
                <tr>
                  <td colSpan="3" style={{
                    border: '1px solid #cbd5e1',
                    padding: '30px',
                    textAlign: 'center',
                    color: '#64748b',
                    fontStyle: 'italic',
                    fontSize: '14px',
                    backgroundColor: '#f8fafc'
                  }}>
                    No stock data found for the selected criteria
                  </td>
                </tr>
              ) : (
                sortedData.map((item, index) => (
                  <tr key={`${item.itemDescription}-${item.location}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                    <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
                    <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.itemDescription || 'Unknown Product'}</td>
                    <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.qty.toFixed(2)}</td>
                  </tr>
                ))
              )}
              {sortedData.length > 0 && (
                <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
                  <td colSpan="2" style={{
                    border: '1px solid #cbd5e1',
                    padding: '10px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    Total
                  </td>
                  <td style={{
                    border: '1px solid #cbd5e1',
                    padding: '10px',
                    textAlign: 'right',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>{totalQty.toFixed(2)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Default detailed view
  return (
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      marginTop: '20px',
      border: '2px solid #3b82f6',
      borderRadius: '12px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      pageBreakInside: 'avoid',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }} className="print-only">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            margin: '0', 
            marginBottom: '15px',
            color: '#1e293b',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}>
            {branchProfile.branchName}
          </h1>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
            <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
              {reportTitle}
            </div>
            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
              <span><strong>Location:</strong> {filters.location.includes("All Warehouse") ? "All Warehouse" : filters.location.join(", ")}</span>
              <span><strong>Date:</strong> {new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium' })}</span>
            </div>
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '15px',
          textAlign: 'center',
          minWidth: '150px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{city}</div>
          <div style={{ fontSize: '12px', marginTop: '5px', opacity: '0.9' }}>
            Option: {formData.reportType === 'detailed' ? 'Detail' : 'Summary'}
          </div>
        </div>
      </div>

      {/* Stock Table
      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse', 
          fontSize: '11px', 
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Location</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Item Description</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Group Name</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Category</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Brand</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>UOM</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Qty</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Tax (%)</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Tax Amount</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>MRP</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Value</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan="12" style={{
                  border: '1px solid #cbd5e1',
                  padding: '30px',
                  textAlign: 'center',
                  color: '#64748b',
                  fontStyle: 'italic',
                  fontSize: '14px',
                  backgroundColor: '#f8fafc'
                }}>
                  No stock data found for the selected criteria
                </td>
              </tr>
            ) : (
              sortedData.map((item, index) => (
                <tr key={`${item.itemDescription}-${item.location}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.location || 'Unknown Location'}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.itemDescription || 'Unknown Product'}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.groupName || 'No Group Assigned'}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.category || 'Unknown Category'}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.brand || 'Unknown Brand'}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.uom || ''}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.qty.toFixed(2)}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.tax.toFixed(2)}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.taxAmount.toFixed(2)}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.mrp.toFixed(2)}</td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{item.value.toFixed(2)}</td>
                </tr>
              ))
            )}
            {sortedData.length > 0 && (
              <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
                <td colSpan="7" style={{
                  border: '1px solid #cbd5e1',
                  padding: '10px',
                  textAlign: 'center',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  Grand Total
                </td>
                <td style={{
                  border: '1px solid #cbd5e1',
                  padding: '10px',
                  textAlign: 'right',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>{totalQty.toFixed(2)}</td>
                <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}></td>
                <td style={{
                  border: '1px solid #cbd5e1',
                  padding: '10px',
                  textAlign: 'right',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>{totalTaxAmount.toFixed(2)}</td>
                <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '12px', fontWeight: '600' }}></td>
                <td style={{
                  border: '1px solid #cbd5e1',
                  padding: '10px',
                  textAlign: 'right',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>{totalValue.toFixed(2)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div> */}

      {/* Summary Section
      {sortedData.length > 0 && (
        <div style={{ 
          marginTop: '30px', 
          padding: '20px', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '15px',
            color: '#1e293b'
          }}>Summary</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '15px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Records</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{sortedData.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Quantity</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{totalQty.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Tax Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#d97706' }}>{totalTaxAmount.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Value</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>₹{totalValue.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

// Main Stock Report Generator Component
const StockReportGenerator = () => {
  const [type, setType] = useState('-SELECT-');
  const [stockType, setStockType] = useState('-SELECT-');
  const [reportType, setReportType] = useState('detailed');
  const [filters, setFilters] = useState({
    location: ["All Warehouse"],
    group: ["All Groups"],
    product: ["All Products"]
  });
  const [locations, setLocations] = useState(["All Warehouse", "WAREHOUSE"]);
  const [groups, setGroups] = useState(["All Groups"]);
  const [categories, setCategories] = useState(["All Categories"]);
  const [brands, setBrands] = useState(["All Brands"]);
  const [products, setProducts] = useState(["All Products"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: ""
  });
  const [stockData, setStockData] = useState([]);
  const [productGroupMap, setProductGroupMap] = useState({});
  const [productCategoryMap, setProductCategoryMap] = useState({});
  const [productBrandMap, setProductBrandMap] = useState({});

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadFirebaseData = (user) => {
    const encodedEmail = encodeEmailForPath(user.email);

    // Load Branch Profile
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
          phone: latestProfile.mobileNo || '',
          city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`)
        });
      }
    });

    // Load Locations (Stores/Warehouses)
    const storesRef = ref(database, `users/${encodedEmail}/stores`);
    onValue(storesRef, (snapshot) => {
      const locationsData = ["All Warehouse"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const store = childSnapshot.val();
          if (store.name && !locationsData.includes(store.name)) {
            locationsData.push(store.name);
          }
        });
      }
      setLocations(locationsData);
      if (!filters.location.length) {
        setFilters(prev => ({ ...prev, location: [locationsData[0]] }));
      }
    });

    // Load Groups
    const groupsRef = ref(database, `users/${encodedEmail}/productGroups`);
    onValue(groupsRef, (snapshot) => {
      const groupsData = ["All Groups"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const group = childSnapshot.val();
          if (group.name && !groupsData.includes(group.name)) {
            groupsData.push(group.name);
          }
        });
      }
      setGroups(groupsData);
      if (!filters.group.length) {
        setFilters(prev => ({ ...prev, group: [groupsData[0]] }));
      }
    });

    // Load Categories
    const categoriesRef = ref(database, `users/${encodedEmail}/categories`);
    onValue(categoriesRef, (snapshot) => {
      const categoriesData = ["All Categories"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const category = childSnapshot.val();
          if (category.name && !categoriesData.includes(category.name)) {
            categoriesData.push(category.name);
          }
        });
      }
      setCategories(categoriesData);
      if (!filters.category?.length) {
        setFilters(prev => ({ ...prev, category: [categoriesData[0]] }));
      }
    });

    // Load Brands
    const brandsRef = ref(database, `users/${encodedEmail}/brands`);
    onValue(brandsRef, (snapshot) => {
      const brandsData = ["All Brands"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const brand = childSnapshot.val();
          if (brand.name && !brandsData.includes(brand.name)) {
            brandsData.push(brand.name);
          }
        });
      }
      setBrands(brandsData);
      if (!filters.brand?.length) {
        setFilters(prev => ({ ...prev, brand: [brandsData[0]] }));
      }
    });

    // Load Products and their mappings
    const productsRef = ref(database, `users/${encodedEmail}/products`);
    onValue(productsRef, (snapshot) => {
      const productsData = ["All Products"];
      const groupMap = {};
      const categoryMap = {};
      const brandMap = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const product = childSnapshot.val();
          if (product.productName && !productsData.includes(product.productName)) {
            productsData.push(product.productName);
            groupMap[product.productName] = product.group || 'No Group Assigned';
            categoryMap[product.productName] = product.category || 'Unknown Category';
            brandMap[product.productName] = product.brand || 'Unknown Brand';
          }
        });
      }
      setProducts(productsData);
      setProductGroupMap(groupMap);
      setProductCategoryMap(categoryMap);
      setProductBrandMap(brandMap);
      if (!filters.product.length) {
        setFilters(prev => ({ ...prev, product: [productsData[0]] }));
      }
    });

    // Load Opening Stock Data
    const stocksRef = ref(database, `users/${encodedEmail}/openingStock`);
    onValue(stocksRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const stockRecords = [];
        
        Object.entries(data).forEach(([stockId, stockData]) => {
          if (stockData.items && Array.isArray(stockData.items)) {
            stockData.items.forEach(item => {
              if (item.itemDescription) {
                stockRecords.push({
                  stockId: stockId,
                  refNo: stockData.refNo || 'N/A',
                  refDate: stockData.refDate || '',
                  location: stockData.location || 'Unknown Location',
                  itemDescription: item.itemDescription || 'Unknown Product',
                  groupName: productGroupMap[item.itemDescription] || 'No Group Assigned',
                  category: productCategoryMap[item.itemDescription] || 'Unknown Category',
                  brand: productBrandMap[item.itemDescription] || 'Unknown Brand',
                  uom: item.uom || '',
                  qty: parseFloat(item.qty) || 0,
                  mrp: parseFloat(item.mrp) || 0,
                  value: parseFloat(item.value) || 0,
                  tax: parseFloat(item.tax) || 0,
                  taxAmount: parseFloat(item.taxAmount) || 0,
                });
              }
            });
          }
        });
        
        setStockData(stockRecords);
      } else {
        setStockData([]);
      }
    });

    return () => {
      off(branchProfileRef);
      off(storesRef);
      off(groupsRef);
      off(categoriesRef);
      off(brandsRef);
      off(productsRef);
      off(stocksRef);
    };
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    setShowReport(true);
  };

  const handlePrint = () => {
    const printContent = (
      <StockReportDisplay 
        formData={{ type, stockType, reportType }}
        filters={filters}
        branchProfile={branchProfile}
        stockData={stockData}
      />
    );

    const htmlString = ReactDOMServer.renderToString(printContent);

    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Opening Stock Report</title>
          <style>
            body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
            @page { size: A4; margin: 5mm; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            .print-only { display: block !important; }
          </style>
        </head>
        <body>
          <div id="print-content">${htmlString}</div>
          <script>
            setTimeout(function() {
              window.print();
              window.close();
            }, 500);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formData = {
    type,
    stockType,
    reportType
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div>Loading Stock Data...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem', 
        color: '#64748b', 
        fontSize: '18px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{ color: '#1e293b', marginBottom: '16px' }}>Authentication Required</h2>
        <p>Please login to access the Stock Report Generator</p>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .print-only, .print-only * {
              visibility: visible;
            }
            .print-only {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 20px;
              box-shadow: none;
              border: none;
            }
          }
        `}
      </style>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>Opening Stock Report Generator</h1>
      </div>
      
      {/* Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="-SELECT-">-SELECT-</option>
            <option value="Productwise">Productwise</option>
           
          </select>
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Stock Type</label>
          <select
            value={stockType}
            onChange={(e) => setStockType(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="-SELECT-">-SELECT-</option>
            <option value="with zero">With Zero</option>
            <option value="with out zero">With Out Zero</option>
          </select>
        </div>
      </div>

      {/* Filter Checkboxes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        <FilterSection 
          title="Warehouse" 
          options={locations} 
          selected={filters.location} 
          onChange={(value) => handleFilterChange("location", value)}
        />
        <FilterSection 
          title="Groups" 
          options={groups} 
          selected={filters.group} 
          onChange={(value) => handleFilterChange("group", value)}
        />
        <FilterSection 
          title="Product" 
          options={products} 
          selected={filters.product} 
          onChange={(value) => handleFilterChange("product", value)}
        />
      </div>
  
      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Print Report
        </button>
      </div>
  
      {/* Stock Report Display */}
      {showReport && stockData && (
        <StockReportDisplay 
          formData={formData}
          filters={filters}
          branchProfile={branchProfile}
          stockData={stockData}
        />
      )}
    </div>
  );
};
  
export default StockReportGenerator;